import React, { useMemo, useState, useRef, useEffect } from 'react';

function isEmpty(val) {
  return val == null || String(val).trim() === '';
}

/**
 * <select> preset list + "+ Add new entry…" for arbitrary text (same pattern as Admin Tune Metadata).
 */
export default function TuneFieldSelectWithAdd({
  value,
  options = [],
  onSave,
  fieldLabel = 'value',
  highlightEmpty = false,
  className,
  id,
  allowAddNew = true,
}) {
  const [adding, setAdding] = useState(false);
  const [newVal, setNewVal] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    if (adding && inputRef.current) inputRef.current.focus();
  }, [adding]);

  const allOpts = useMemo(() => {
    const s = new Set(options);
    if (allowAddNew && !isEmpty(value)) s.add(String(value).trim());
    return [...s].sort((a, b) => a.localeCompare(b));
  }, [options, value, allowAddNew]);

  const commitNew = () => {
    const v = newVal.trim();
    if (v) onSave(v);
    setAdding(false);
    setNewVal('');
  };

  const empty = isEmpty(value);
  const borderStyle =
    highlightEmpty && empty ? '1.5px solid #f87171' : '1px solid var(--color-border, #d0d5dd)';

  if (adding) {
    return (
      <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
        <input
          ref={inputRef}
          id={id}
          value={newVal}
          onChange={(e) => setNewVal(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              commitNew();
            }
            if (e.key === 'Escape') {
              setAdding(false);
              setNewVal('');
            }
          }}
          onBlur={commitNew}
          placeholder={`New ${fieldLabel}…`}
          className={className}
          style={{
            flex: 1,
            padding: '5px 8px',
            border: '1.5px solid #7c3aed',
            borderRadius: 6,
            background: 'var(--color-surface, var(--color-background))',
            color: 'inherit',
            fontSize: 'inherit',
            outline: 'none',
          }}
        />
        <button
          type="button"
          onMouseDown={(e) => {
            e.preventDefault();
            setAdding(false);
            setNewVal('');
          }}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            fontSize: 14,
            color: '#94a3b8',
            padding: '0 2px',
          }}
        >
          ✕
        </button>
      </div>
    );
  }

  const selectStyle = className
    ? {
        width: '100%',
        cursor: 'pointer',
        ...(highlightEmpty && empty ? { border: '1.5px solid #f87171' } : {}),
      }
    : {
        width: '100%',
        padding: '5px 6px',
        border: borderStyle,
        borderRadius: 6,
        background: 'var(--color-surface, var(--color-background))',
        color: 'inherit',
        fontSize: 12,
        outline: 'none',
        cursor: 'pointer',
      };

  return (
    <select
      id={id}
      className={className}
      value={value || ''}
      onChange={(e) => {
        if (e.target.value === '__add__') setAdding(true);
        else onSave(e.target.value);
      }}
      style={selectStyle}
    >
      <option value="">{allowAddNew ? '— select or add new —' : '— select —'}</option>
      {allOpts.map((o) => (
        <option key={o} value={o}>
          {o}
        </option>
      ))}
      {allowAddNew ? (
        <>
          <option disabled style={{ color: '#94a3b8' }}>
            ──────────
          </option>
          <option value="__add__">+ Add new entry…</option>
        </>
      ) : null}
    </select>
  );
}
